import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { newImageElement } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import { useSubscription } from "../context/SubscriptionContext";
import TopUpModal from "../components/TopUpModal";
import SaveToLibraryModal from "../components/SaveToLibraryModal";
import SaveToWorkspaceModal from "../components/SaveToWorkspaceModal";
import GuestAuthModal from "../components/GuestAuthModal";
import { DesignBrainPanel, DesignFeedback } from "../components/DesignBrain";
import { CreativeDirectorPicker } from "../components/CreativeDirectors";
import CreativeDirectorManager from "../components/CreativeDirectorManager";
import { StylesLibraryBrowser } from "../components/StylesLibrary";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Sparkle, Image as ImageIcon, Star, UsersThree, X, Crown,
  DownloadSimple, PencilLine, Lightning, ArrowClockwise, CircleNotch, Lock, ClockCounterClockwise, UploadSimple, Images, BookmarkSimple, FilmSlate, FolderOpen,
} from "@phosphor-icons/react";

const readPreview = (file) => URL.createObjectURL(file);

const ASPECTS = ["1:1", "4:5", "9:16", "16:9", "2:3", "3:2", "4:3", "3:4", "21:9"]; // eslint-disable-line no-unused-vars
// Visual aspect-ratio options — same underlying ratio values, now with a proportion shape + label.
const ASPECT_OPTIONS = [
  { v: "1:1", label: "Square" },
  { v: "4:5", label: "Vertical" },
  { v: "9:16", label: "Tall / Story" },
  { v: "16:9", label: "Horizontal" },
  { v: "2:3", label: "Vertical" },
  { v: "3:2", label: "Horizontal" },
  { v: "4:3", label: "Horizontal" },
  { v: "3:4", label: "Vertical" },
  { v: "21:9", label: "Wide" },
];

// Renders a rectangle drawn to the exact proportions of the ratio (uses currentColor for the stroke).
function RatioShape({ v }) {
  const [w, h] = v.split(":").map(Number);
  const MAX = 22;
  const scale = MAX / Math.max(w, h);
  const iw = Math.max(5, Math.round(w * scale));
  const ih = Math.max(5, Math.round(h * scale));
  return (
    <span className="flex items-center justify-center" style={{ width: 24, height: 24 }}>
      <span className="block rounded-[2px] border-2 border-current" style={{ width: iw, height: ih }} />
    </span>
  );
}

const TIERS = [
  // credits = platform credits consumed per successful generation at this tier.
  // 4K retired (2026-02). Server also enforces these prices — see AIPRO_TIERS in server.py.
  { key: "1k", label: "Best for social media", res: "1K", credits: 1, proOnly: false },
  { key: "2k", label: "Best for printing",     res: "2K", credits: 4, proOnly: true  },
];

function UploadBox({ id, icon: Icon, label, hint, accent, multiple, max, files, onAdd, onRemove, busy, busyLabel }) {
  const inputRef = useRef(null);
  const onPick = (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length) onAdd(picked);
    e.target.value = "";
  };
  const onDrop = (e) => {
    e.preventDefault();
    const dropped = Array.from(e.dataTransfer.files || []).filter((f) => f.type.startsWith("image/"));
    if (dropped.length) onAdd(dropped);
  };
  return (
    <div
      data-testid={`upload-box-${id}`}
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
      className="relative rounded-2xl border border-white/10 bg-[#111] p-4 flex flex-col"
    >
      {busy && (
        <div data-testid={`busy-${id}`} className="absolute inset-0 z-10 rounded-2xl bg-black/75 backdrop-blur-sm flex flex-col items-center justify-center gap-2">
          <CircleNotch size={26} className="animate-spin text-amber-300" />
          <p className="text-xs font-semibold text-amber-200">{busyLabel || "Working…"}</p>
        </div>
      )}
      <div className="flex items-center gap-2 mb-3">
        <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${accent}`}><Icon size={17} weight="fill" className="text-white" /></div>
        <div className="min-w-0">
          <p className="text-sm font-semibold leading-tight truncate">{label}</p>
          <p className="text-[11px] text-zinc-500 leading-tight">{hint}</p>
        </div>
        {multiple && <span className="ml-auto text-[10px] font-semibold text-zinc-400">{files.length}{max ? `/${max}` : ""}</span>}
      </div>

      {files.length > 0 && (
        <div className={`grid ${multiple ? "grid-cols-4" : "grid-cols-1"} gap-2 mb-3`}>
          {files.map((f, i) => (
            <div key={i} className="relative group rounded-lg overflow-hidden ring-1 ring-white/10 aspect-square bg-black/40">
              <img src={readPreview(f)} alt={f.name} className="w-full h-full object-cover" />
              <button data-testid={`remove-${id}-${i}`} onClick={() => onRemove(i)} className="absolute top-1 right-1 h-5 w-5 rounded-full bg-black/70 hover:bg-red-600 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><X size={11} className="text-white" /></button>
            </div>
          ))}
        </div>
      )}

      {(multiple || files.length === 0) && (!max || files.length < max) && (
        <button
          data-testid={`pick-${id}`}
          onClick={() => inputRef.current?.click()}
          className="mt-auto w-full rounded-lg border border-dashed border-white/15 hover:border-fuchsia-500/60 hover:bg-white/[0.03] py-3 text-xs font-medium text-zinc-400 hover:text-white transition-colors"
        >
          {files.length === 0 ? "Click or drag to upload" : "+ Add more"}
        </button>
      )}
      <input ref={inputRef} type="file" accept="image/*" multiple={multiple} onChange={onPick} className="hidden" data-testid={`input-${id}`} />
    </div>
  );
}

// Reference Flyer box with TWO clear options: Upload from Computer + Choose from Library.
// Both feed the same single reference slot.
function ReferenceBox({ file, onPickFile, onRemove, onOpenLibrary }) {
  const inputRef = useRef(null);
  const onPick = (e) => {
    const f = Array.from(e.target.files || [])[0];
    if (f) onPickFile(f);
    e.target.value = "";
  };
  const onDrop = (e) => {
    e.preventDefault();
    const f = Array.from(e.dataTransfer.files || []).filter((x) => x.type.startsWith("image/"))[0];
    if (f) onPickFile(f);
  };
  return (
    <div
      data-testid="upload-box-aipro-reference"
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
      className="relative rounded-2xl border border-white/10 bg-[#111] p-4 flex flex-col"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="h-8 w-8 rounded-lg flex items-center justify-center bg-fuchsia-600"><ImageIcon size={17} weight="fill" className="text-white" /></div>
        <div className="min-w-0">
          <p className="text-sm font-semibold leading-tight truncate">Reference Flyer</p>
          <p className="text-[11px] text-zinc-500 leading-tight">1 image · the style to emulate</p>
        </div>
      </div>

      {file && (
        <div className="grid grid-cols-1 gap-2 mb-3">
          <div className="relative group rounded-lg overflow-hidden ring-1 ring-white/10 aspect-square bg-black/40">
            <img src={readPreview(file)} alt={file.name} className="w-full h-full object-cover" />
            <button data-testid="remove-aipro-reference-0" onClick={onRemove} className="absolute top-1 right-1 h-5 w-5 rounded-full bg-black/70 hover:bg-red-600 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><X size={11} className="text-white" /></button>
          </div>
        </div>
      )}

      <div className="mt-auto grid grid-cols-2 gap-2">
        <button
          data-testid="aipro-reference-upload"
          onClick={() => inputRef.current?.click()}
          className="rounded-lg border border-dashed border-white/15 hover:border-fuchsia-500/60 hover:bg-white/[0.03] py-2.5 px-1 text-[11px] font-semibold text-zinc-300 hover:text-white transition-colors inline-flex flex-col items-center justify-center gap-1 text-center"
        >
          <UploadSimple size={16} weight="bold" /> Upload from Computer
        </button>
        <button
          data-testid="aipro-reference-library"
          onClick={onOpenLibrary}
          className="rounded-lg border border-fuchsia-500/40 bg-fuchsia-500/10 hover:bg-fuchsia-500/20 py-2.5 px-1 text-[11px] font-semibold text-fuchsia-200 transition-colors inline-flex flex-col items-center justify-center gap-1 text-center"
        >
          <Images size={16} weight="fill" /> Choose from Library
        </button>
      </div>
      <input ref={inputRef} type="file" accept="image/*" onChange={onPick} className="hidden" data-testid="input-aipro-reference" />
    </div>
  );
}

function Paywall({ open, trigger, onClose, onUpgrade, onTopup }) {
  if (!open) return null;
  const headline = trigger === "pro_tier" ? "2K is a Pro feature" : "You're out of AI credits";
  const sub = trigger === "pro_tier"
    ? "Print-quality 2K exports are included with Pro. Upgrade, or grab a one-time credit pack."
    : "You've used your free AI credits. Upgrade to Pro for a monthly allowance, or top up instantly.";
  return (
    <div data-testid="aipro-paywall" className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-3xl border border-white/10 bg-[#0E0E0E] p-7 shadow-2xl text-center">
        <button data-testid="aipro-paywall-close" onClick={onClose} className="absolute top-5 right-5 text-zinc-500 hover:text-white transition-colors"><X size={20} /></button>
        <div className="mx-auto h-14 w-14 rounded-2xl bg-amber-400/15 ring-1 ring-amber-400/30 flex items-center justify-center mb-4">
          <Crown weight="fill" size={26} className="text-amber-300" />
        </div>
        <h2 data-testid="aipro-paywall-headline" className="font-display font-black text-2xl tracking-tight mb-2">{headline}</h2>
        <p className="text-sm text-zinc-400 mb-6">{sub}</p>
        <div className="flex flex-col gap-3">
          <button data-testid="aipro-paywall-upgrade" onClick={onUpgrade} className="w-full h-12 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 text-black font-bold transition-transform hover:scale-[1.02] inline-flex items-center justify-center gap-2">
            <Crown weight="fill" size={18} /> Upgrade to Pro
          </button>
          <button data-testid="aipro-paywall-topup" onClick={onTopup} className="w-full h-12 rounded-full bg-white/10 hover:bg-white/20 text-white font-bold transition-colors inline-flex items-center justify-center gap-2">
            <Lightning weight="fill" size={18} style={{ color: "#FFD60A" }} /> Buy More Credits
          </button>
        </div>
      </div>
    </div>
  );
}

// Small, hoisted row primitive for the debug table below.
function DebugRow({ label, value, testid }) {
  return (
    <div className="flex items-start justify-between gap-3 text-[11px] py-0.5">
      <span className="text-zinc-500">{label}</span>
      <span data-testid={testid} className="font-mono text-zinc-200 text-right break-all">{String(value ?? "—")}</span>
    </div>
  );
}

// Developer Debug Panel — shown when the user is an admin OR the URL contains
// ?debug=1. Renders the resolved generation payload via /api/aipro/debug-payload
// (which does NOT call OpenAI and does NOT charge credits). Purely observational.
function DevDebugPanel({
  isAdmin, reference, hero, supporting, eventDetails, direction, aspect, tier,
  generationMode, director, debugPayload, setDebugPayload, debugLoading, setDebugLoading,
  referenceInfluence, brandId, paletteId,
}) {
  const [open, setOpen] = useState(false);
  const urlDebug = typeof window !== "undefined"
    && new URLSearchParams(window.location.search).get("debug") === "1";
  const visible = isAdmin || urlDebug;
  if (!visible) return null;

  const fetchPayload = async () => {
    setDebugLoading(true);
    try {
      const fd = new FormData();
      if (reference[0]) fd.append("reference", reference[0]);
      hero.forEach((f) => fd.append("hero", f));
      supporting.forEach((f) => fd.append("supporting", f));
      fd.append("event_details", eventDetails);
      fd.append("design_direction", direction);
      fd.append("aspect_ratio", aspect);
      fd.append("quality_tier", tier);
      fd.append("creative_director_id", director?.id || "");
      fd.append("generation_mode", generationMode);
      fd.append("reference_influence", String(referenceInfluence));
      fd.append("brand_id", brandId || "");
      fd.append("palette_id", paletteId || "");
      const res = await api.post("/aipro/debug-payload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDebugPayload(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Debug preview failed.");
    } finally {
      setDebugLoading(false);
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text || "");
      toast.success("Copied to clipboard.");
    } catch (_) { toast.error("Clipboard blocked by browser."); }
  };

  return (
    <div
      data-testid="aipro-debug-panel"
      className="rounded-xl border border-white/10 bg-black/60 p-3.5 mt-1 text-white"
    >
      <button
        type="button"
        data-testid="aipro-debug-panel-toggle"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between text-left"
      >
        <span className="text-[11px] uppercase tracking-[0.16em] font-bold text-amber-300">
          Developer Debug: Final Generation Payload
        </span>
        <span className="text-[11px] text-zinc-400">{open ? "Hide ▲" : "Show ▼"}</span>
      </button>
      {open && (
        <div className="mt-3 flex flex-col gap-3">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              data-testid="aipro-debug-refresh"
              onClick={fetchPayload}
              disabled={debugLoading}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-white/10 hover:bg-white/20 text-xs font-bold disabled:opacity-60"
            >
              {debugLoading ? <CircleNotch size={12} className="animate-spin" /> : <ArrowClockwise size={12} weight="bold" />}
              Preview payload
            </button>
            {debugPayload && (
              <>
                <button
                  type="button"
                  data-testid="aipro-debug-copy-prompt"
                  onClick={() => copy(debugPayload.final_prompt_full || debugPayload.final_prompt || "")}
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-white/10 hover:bg-white/20 text-xs font-bold"
                >
                  Copy Final Prompt
                </button>
                <button
                  type="button"
                  data-testid="aipro-debug-copy-payload"
                  onClick={() => copy(JSON.stringify(debugPayload, null, 2))}
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-white/10 hover:bg-white/20 text-xs font-bold"
                >
                  Copy Full Payload
                </button>
              </>
            )}
          </div>
          {debugPayload ? (
            <div className="rounded-lg bg-black/40 ring-1 ring-white/10 p-3">
              <DebugRow testid="dbg-mode"          label="Generation mode"      value={debugPayload.mode} />
              <DebugRow testid="dbg-endpoint"      label="OpenAI endpoint"      value={debugPayload.endpoint} />
              <DebugRow testid="dbg-ref-included"  label="Reference included"   value={debugPayload.reference_included ? "yes" : "no"} />
              <DebugRow testid="dbg-ref-dropped"   label="Reference dropped"    value={debugPayload.reference_provided_but_dropped ? "yes" : "no"} />
              <DebugRow testid="dbg-hero"          label="Hero count"           value={debugPayload.hero_count} />
              <DebugRow testid="dbg-supporting"    label="Supporting count"     value={debugPayload.supporting_count} />
              <DebugRow testid="dbg-director"      label="Creative Director"    value={debugPayload.creative_director_name || "—"} />
              <DebugRow testid="dbg-prompt-name"   label="Selected prompt"      value={debugPayload.prompt_name} />
              <DebugRow testid="dbg-utd"           label="Universal Technical Director" value={debugPayload.universal_technical_director_included ? "yes" : "no"} />
              <DebugRow testid="dbg-legacy-dd"     label="Legacy default direction"     value={debugPayload.legacy_default_direction_included ? "yes" : "no"} />
              <DebugRow testid="dbg-legacy-refstyle" label="Legacy reference-style logic" value={debugPayload.legacy_reference_style_included ? "yes" : "no"} />
              <DebugRow testid="dbg-prompt-len"    label="Prompt length"        value={debugPayload.prompt_length} />
              <DebugRow testid="dbg-prompt-trunc"  label="Prompt truncated"     value={debugPayload.prompt_truncated ? "YES" : "no"} />
              <DebugRow testid="dbg-size"          label="Render size"          value={debugPayload.size} />
              <div className="mt-3">
                <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Final prompt</p>
                <pre
                  data-testid="dbg-final-prompt"
                  className="max-h-64 overflow-auto whitespace-pre-wrap text-[11px] leading-snug text-zinc-200 bg-black/60 ring-1 ring-white/10 rounded-md p-2.5"
                >{debugPayload.final_prompt}</pre>
              </div>
            </div>
          ) : (
            <p className="text-[11px] text-zinc-500">Click <b>Preview payload</b> to see the resolved prompt + payload for the current inputs. No credits are charged.</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function AiProStudio() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isPro, openUpgrade, sub: subInfo, refresh: refreshSub } = useSubscription();
  const isAdmin = !!user?.is_admin;
  const canUsePro = isPro || isAdmin;

  const [reference, setReference] = useState([]);
  const [amateur, setAmateur] = useState([]);
  const [hero, setHero] = useState([]);
  const [supporting, setSupporting] = useState([]);
  const [extracting, setExtracting] = useState(false);
  const [eventDetails, setEventDetails] = useState("");
  const [direction, setDirection] = useState("");
  const [aspect, setAspect] = useState("4:5");
  const [tier, setTier] = useState("1k");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [opening, setOpening] = useState(false);
  const [topupOpen, setTopupOpen] = useState(false);
  const [paywall, setPaywall] = useState({ open: false, trigger: "" });
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [saveLibOpen, setSaveLibOpen] = useState(false);
  const [saveWsOpen, setSaveWsOpen] = useState(false);
  const [brainConfig, setBrainConfig] = useState(null);
  // AI Design Brain / Layout Engine — hidden + disabled by default (2026-07).
  // Plumbing preserved: to re-enable, flip this initial state to `true` AND
  // un-hide the <DesignBrainPanel /> render below.
  const [smartLayout, setSmartLayout] = useState(false);
  const [director, setDirector] = useState(null);
  const [directorManagerOpen, setDirectorManagerOpen] = useState(false);
  const [directorVersion, setDirectorVersion] = useState(0);
  // Generation Strategy — three isolated modes:
  //   "reference"         — Reference-Driven (existing behavior; reference REQUIRED)
  //   "creative_director" — Pure Text Mode (no images sent; text-to-image)
  //   "hybrid"            — Prompt-led with optional reference influence
  // Defaults to "reference" so any existing workflow is preserved verbatim.
  const [generationMode, setGenerationMode] = useState("reference");
  // Reference Influence Slider — only visible in Hybrid Mode with a reference
  // image uploaded. Values map to prompt language on the backend:
  //   0=inspiration · 25=light · 50=moderate · 75=strong · 100=close
  const [referenceInfluence, setReferenceInfluence] = useState(50);
  // Brand System + Color Palette (authenticated user's private workspace).
  // Both are optional — passing an empty id means "none applied".
  const [brands, setBrands] = useState([]);
  const [palettes, setPalettes] = useState([]);
  const [brandId, setBrandId] = useState("");
  const [paletteId, setPaletteId] = useState("");
  // Developer debug panel — visible when the user is an admin OR the URL
  // contains ?debug=1. Purely observational; no behavioral coupling.
  const [debugPayload, setDebugPayload] = useState(null);
  const [debugLoading, setDebugLoading] = useState(false);
  // Lazy Registration (guest) state — the workspace is fully accessible to
  // unauthenticated visitors. They can generate ONCE for free. Any attempt to
  // download, save, export, rerun, or generate again opens the sign-up modal.
  //   • guestSession: server-issued session state from /api/guest/session.
  //     The authoritative session id lives in an HttpOnly signed cookie —
  //     the client only sees the sanitized public state.
  //   • guestUsed: mirrors guestSession.free_render_used for readability.
  //   • guestAuth: { open, trigger, pendingAction } — modal control.
  const isGuest = !user;
  const [guestSession, setGuestSession] = useState(null);
  const [guestUsed, setGuestUsed] = useState(false);
  const [guestAuth, setGuestAuth] = useState({ open: false, trigger: "", pendingAction: null });
  const location = useLocation();

  const remaining = subInfo?.ai_remaining ?? 0;

  // Establish (or refresh) the server-issued guest session on mount. The
  // backend sets an HttpOnly signed cookie in the response; we only see the
  // public state. Any pre-existing legacy `flyerstudio_guest_id` in
  // localStorage is retired here so the browser stops hoarding stale data.
  useEffect(() => {
    if (user) return; // authenticated users don't need a guest session
    // Clean up the legacy client-generated guest id (previous funnel).
    try { localStorage.removeItem("flyerstudio_guest_id"); } catch (_) { /* no-op */ }
    let cancelled = false;
    api.post("/guest/session")
      .then((r) => {
        if (cancelled) return;
        setGuestSession(r.data);
        setGuestUsed(!!r.data?.free_render_used);
      })
      .catch(() => { /* fail open — the server will mint on demand */ });
    return () => { cancelled = true; };
  }, [user]);

  // Hydrate the "latest guest generation" preview so refreshing the page
  // doesn't lose the flyer they just made. The server also refreshes
  // free_render_used from the authoritative db row — the client can't
  // "un-use" the free credit by clearing state.
  useEffect(() => {
    if (user || !guestSession?.session_id) return;
    api.get("/aipro/guest-state")
      .then((r) => {
        setGuestUsed(!!r.data?.used);
        if (r.data?.latest && !result) setResult(r.data.latest);
      })
      .catch(() => {});
  }, [user, guestSession?.session_id, result]);

  // Keep prior renders viewable so no flyer is ever lost, even across a page refresh.
  // History + layout-engine config require an authenticated session; guests
  // hydrate their in-progress flyer separately via /aipro/guest-state.
  useEffect(() => {
    if (!user) return;
    api.get("/aipro/history").then((r) => setHistory(r.data || [])).catch(() => {});
    api.get("/layout-engine/config").then((r) => setBrainConfig(r.data)).catch(() => setBrainConfig({ enabled: false }));
  }, [user]);

  // Load the user's private brand systems + color palettes so the Studio can
  // apply them to a generation. Default brand (if any) is preselected once.
  const brandsLoadedRef = useRef(false);
  useEffect(() => {
    if (!user) return;
    Promise.all([
      api.get("/workspace/brands").then((r) => r.data.brands || []).catch(() => []),
      api.get("/workspace/palettes").then((r) => r.data.palettes || []).catch(() => []),
    ]).then(([b, p]) => {
      setBrands(b);
      setPalettes(p);
      if (!brandsLoadedRef.current) {
        brandsLoadedRef.current = true;
        const def = b.find((x) => x.is_default);
        if (def) setBrandId(def.id);
      }
    });
  }, [user]);

  // Fetch a chosen library style and drop it into the SAME single reference slot.
  const loadLibraryStyleAsReference = useCallback(async (style) => {
    if (!style?.storage_path) return;
    try {
      const resp = await fetch(fileUrl(style.storage_path), { cache: "no-store" });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const ext = (blob.type || "").includes("png") ? "png" : "jpg";
      const safe = (style.title || "reference").replace(/[^a-z0-9]+/gi, "-").slice(0, 40) || "reference";
      const file = new File([blob], `${safe}.${ext}`, { type: blob.type || "image/jpeg" });
      setReference([file]);
      toast.success(`"${style.title}" set as your Reference Flyer.`);
    } catch (e) {
      toast.error("Couldn't load that library style. Please try again.");
    }
  }, []);

  // If the user picked a style on the Dashboard ("Use in AI Pro Studio"), preload it here once.
  const preloadedRef = useRef(false);
  useEffect(() => {
    const style = location.state?.libraryStyle;
    if (style && !preloadedRef.current) {
      preloadedRef.current = true;
      loadLibraryStyleAsReference(style);
      window.history.replaceState({}, document.title);
    }
  }, [location.state, loadLibraryStyleAsReference]);

  // Design Library handoff — arriving with a chosen Creative Director preselects it
  // (and switches to CD Pure Text mode). Workspace handoff — reopening a saved
  // project restores its inputs + preview. Both are additive one-shot preloads.
  const handoffRef = useRef(false);
  useEffect(() => {
    if (handoffRef.current) return;
    const ld = location.state?.libraryDirector;
    const wp = location.state?.workspaceProject;
    if (!ld && !wp) return;
    handoffRef.current = true;
    if (ld) {
      setDirector({ ...ld, fromLibrary: true });
      setGenerationMode("creative_director");
      toast.success(`Creative Director "${ld.name}" selected.`);
    } else if (wp) {
      setEventDetails(wp.event_details || "");
      setDirection(wp.custom_instructions || "");
      const s = wp.settings || {};
      if (s.aspect_ratio) setAspect(s.aspect_ratio);
      if (s.generation_mode) setGenerationMode(s.generation_mode);
      if (s.quality_tier === "1k" || s.quality_tier === "2k") setTier(s.quality_tier);
      if (wp.director_id) {
        setDirector({
          id: wp.director_id, name: wp.director?.name || "Saved Director", fromLibrary: true,
          category_id: wp.category_id, subcategory_id: wp.subcategory_id,
          category_name: wp.category?.name || "", subcategory_name: wp.subcategory?.name || "",
        });
      }
      if (wp.image_storage_path) {
        setResult({
          storage_path: wp.image_storage_path, width: s.width, height: s.height,
          aspect_ratio: s.aspect_ratio, quality_tier: s.quality_tier,
        });
      }
      toast.success(`Project "${wp.name}" reopened — pick up where you left off.`);
    }
    window.history.replaceState({}, document.title);
  }, [location.state]);

  // Post-signup handoff — when a lazy-registered guest returns from OAuth,
  // AuthCallback lands them here with ?resume=1 and their pending action in
  // localStorage. We wait until the user is authenticated + the transferred
  // guest generation appears in history, then execute the original click
  // (download the transferred flyer, or trigger the second generation).
  const resumedRef = useRef(false);
  useEffect(() => {
    if (resumedRef.current) return;
    if (!user) return;
    const params = new URLSearchParams(location.search);
    if (params.get("resume") !== "1") return;
    let raw = null;
    try { raw = localStorage.getItem("flyerstudio_guest_pending_action"); } catch (_) { /* no-op */ }
    if (!raw) return;
    let pending = null;
    try { pending = JSON.parse(raw); } catch (_) { /* no-op */ }
    if (!pending) return;
    resumedRef.current = true;
    try { localStorage.removeItem("flyerstudio_guest_pending_action"); } catch (_) { /* no-op */ }

    // Refresh the subscription context so the new 9-credit balance is visible
    // before we execute the pending action.
    (async () => {
      try { await refreshSub?.(); } catch (_) { /* no-op */ }
      // Wait for the transferred guest generation to appear in aipro history
      // so we can hydrate `result` and either download it or open the modal.
      try {
        const hist = await api.get("/aipro/history");
        const transferred = (hist.data || []).find((h) => h.storage_path && h.storage_path.includes("/guest/"));
        if (transferred) setResult(transferred);
        const target = pending.storage_path || transferred?.storage_path;
        if (pending.kind === "download") {
          if (target) {
            const url = `${fileUrl(target)}?download=1&filename=ai-pro-studio-flyer.png`;
            const a = document.createElement("a");
            a.href = url;
            a.download = "ai-pro-studio-flyer.png";
            a.rel = "noopener";
            document.body.appendChild(a);
            a.click();
            setTimeout(() => a.remove(), 1000);
            toast.success("Welcome! Your flyer is downloading — you also unlocked 9 free credits.");
          }
        } else if (pending.kind === "save_workspace") {
          if (transferred) setSaveWsOpen(true);
          toast.success("Welcome! Save your design to the Workspace — you've unlocked 9 free credits.");
        } else if (pending.kind === "save_library") {
          // Save to Library still requires admin; the modal will only open
          // if the user is an admin. Non-admins just get the welcome toast.
          if (user?.is_admin && transferred) setSaveLibOpen(true);
          toast.success("Welcome! Your guest render is saved to your account — 9 free credits added.");
        } else if (pending.kind === "export") {
          // Restart Open-in-Editor. `openInEditor` reads `result` state, which
          // was just set above via `setResult(transferred)`. Give React one
          // tick to flush that state before invoking.
          setTimeout(() => { try { openInEditor(); } catch (_) { /* no-op */ } }, 300);
          toast.success("Welcome! Opening your design in the FlyerStudio Editor.");
        } else if (pending.kind === "generate") {
          toast.success("Welcome! You've unlocked 9 free credits — click Generate to make your next flyer.");
        }
      } catch (_) { /* no-op */ }
    })();
  }, [user, location.search, refreshSub]);

  const addFiles = (setter, limit) => (picked) =>
    setter((prev) => [...prev, ...picked].slice(0, limit));
  const removeAt = (setter) => (i) => setter((prev) => prev.filter((_, idx) => idx !== i));

  // Amateur flyer = TEXT SOURCE ONLY: OCR it and populate Event Details. Its image is never sent to the image model.
  const onAmateurAdd = async (picked) => {
    const file = picked[0];
    if (!file) return;
    setAmateur([file]);
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("amateur", file);
      const res = await api.post("/aipro/extract-text", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const text = (res.data?.text || "").trim();
      if (text) {
        setEventDetails((prev) => (prev.trim() ? prev.trim() + "\n\n" + text : text));
        toast.success("Flyer text extracted into Event Details — review & edit before generating.");
      } else {
        toast.error("No readable text found. Please type the details manually.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Couldn't read the flyer text.");
    } finally {
      setExtracting(false);
    }
  };

  const openPaywall = (trigger) => setPaywall({ open: true, trigger });
  const closePaywall = () => setPaywall({ open: false, trigger: "" });

  const pickTier = (t) => {
    if (t.proOnly && !canUsePro) { openPaywall("pro_tier"); return; }
    setTier(t.key);
  };

  const generate = useCallback(async () => {
    // ---- Lazy Registration guest branch ----
    // Guests can generate ONCE. On the 2nd attempt (or Rerun) the modal opens.
    // Server-side authoritative check via the HttpOnly signed cookie — the
    // client `guestUsed` flag is only used to skip a wasted round-trip when
    // we already know they're out.
    if (isGuest) {
      if (guestUsed) {
        setGuestAuth({ open: true, trigger: "second_generate", pendingAction: { kind: "generate" } });
        return;
      }
      // Per-mode validation mirrors the authenticated flow.
      if (generationMode === "reference" && reference.length === 0) {
        toast.error("Add a Reference Flyer to generate in Reference-Driven Mode.");
        return;
      }

      const fd = new FormData();
      // guest_id no longer sent — the backend reads the signed cookie instead.
      if (reference[0]) fd.append("reference", reference[0]);
      hero.forEach((f) => fd.append("hero", f));
      supporting.forEach((f) => fd.append("supporting", f));
      fd.append("event_details", eventDetails);
      fd.append("design_direction", direction);
      fd.append("aspect_ratio", aspect);
      fd.append("generation_mode", generationMode);
      fd.append("reference_influence", String(referenceInfluence));
      fd.append("creative_director_id", director?.id || "");

      setRunning(true);
      setResult(null);
      try {
        const submit = await api.post("/aipro/guest-generate", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const jobId = submit.data.id;
        let done = false;
        for (let i = 0; i < 80 && !done; i++) {
          await new Promise((r) => setTimeout(r, 3000));
          let st;
          try { st = await api.get(`/aipro/guest-status/${jobId}`); } catch (_) { continue; }
          const s = st.data.status;
          if (s === "ready") {
            setResult(st.data);
            setGuestUsed(true);
            toast.success("Your free flyer is ready!");
            done = true;
          } else if (s === "error") {
            toast.error(st.data.message || "Generation failed. Please try again.");
            done = true;
          }
        }
        if (!done) toast.error("This is taking longer than expected. Please try again.");
      } catch (e) {
        const detail = e?.response?.data?.detail;
        const reason = detail?.reason;
        const message = detail?.message || (typeof detail === "string" ? detail : "Generation failed. Please try again.");
        if (e?.response?.status === 402 && reason === "guest_used") {
          setGuestUsed(true);
          setGuestAuth({ open: true, trigger: "second_generate", pendingAction: { kind: "generate" } });
        } else if (e?.response?.status === 429 && reason === "rate_limited") {
          toast.error(message);
        } else {
          toast.error(message);
        }
      } finally {
        setRunning(false);
      }
      return;
    }

    // ---- Authenticated path (existing behavior, unchanged) ----
    // Per-mode validation. Only Reference-Driven Mode requires a reference
    // upload. Creative Director Pure Text and Hybrid modes can generate with
    // zero uploaded images.
    if (generationMode === "reference" && reference.length === 0) {
      toast.error("Add a Reference Flyer to generate in Reference-Driven Mode.");
      return;
    }
    const tierObj = TIERS.find((t) => t.key === tier);
    if (tierObj?.proOnly && !canUsePro) { openPaywall("pro_tier"); return; }
    const tierCost = tierObj?.credits || 1;
    if (!isAdmin && remaining < tierCost) { openPaywall("no_credits"); return; }

    const fd = new FormData();
    // Reference upload — sent only when the user actually provided one. Backend
    // ignores it entirely in Creative Director Pure Text Mode.
    if (reference[0]) fd.append("reference", reference[0]);
    hero.forEach((f) => fd.append("hero", f));
    supporting.forEach((f) => fd.append("supporting", f));
    fd.append("event_details", eventDetails);
    fd.append("design_direction", direction);
    fd.append("aspect_ratio", aspect);
    fd.append("quality_tier", tier);
    fd.append("use_layout_engine", brainConfig?.enabled && smartLayout ? "true" : "false");
    fd.append("creative_director_id", director?.id || "");
    fd.append("generation_mode", generationMode);
    fd.append("reference_influence", String(referenceInfluence));
    fd.append("brand_id", brandId || "");
    fd.append("palette_id", paletteId || "");

    setRunning(true);
    setResult(null);
    try {
      const submit = await api.post("/aipro/generate", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const jobId = submit.data.id;
      let done = false;
      for (let i = 0; i < 80 && !done; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        let st;
        try { st = await api.get(`/aipro/status/${jobId}`); } catch (_) { continue; }
        const s = st.data.status;
        if (s === "ready") {
          setResult(st.data);
          setHistory((prev) => [st.data, ...prev.filter((h) => h.storage_path !== st.data.storage_path)].slice(0, 24));
          toast.success("Your AI Pro flyer is ready!");
          refreshSub?.();
          done = true;
        } else if (s === "error") {
          toast.error(st.data.message || "Generation failed. Please try again.");
          done = true;
        }
      }
      if (!done) toast.error("This is taking longer than expected. Please try again.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const reason = detail?.reason;
      const message = detail?.message || (typeof detail === "string" ? detail : "Generation failed. Please try again.");
      if (e?.response?.status === 402 && (reason === "pro_tier" || reason === "no_credits")) {
        openPaywall(reason);
      } else {
        toast.error(message);
      }
    } finally {
      setRunning(false);
    }
  }, [reference, hero, supporting, eventDetails, direction, aspect, tier, canUsePro, isAdmin, remaining, refreshSub, brainConfig, smartLayout, director, generationMode, isGuest, guestUsed]);

  // Resilient download. Accepts a storage_path string, a {storage_path} object, an event, or
  // nothing (defaults to the current result) — so no future edit to how it's wired can break it.
  // Uses a SYNCHRONOUS anchor click within the user gesture (no async fetch/blob, which the browser
  // silently blocks once transient activation expires). The backend sends
  // `Content-Disposition: attachment`, so the click forces a PNG download even cross-origin.
  const resolveStoragePath = (x) => {
    if (typeof x === "string") return x;
    if (x && typeof x === "object" && typeof x.storage_path === "string") return x.storage_path;
    return null;
  };

  const downloadFlyer = (pathOrItem) => {
    const path = resolveStoragePath(pathOrItem) || result?.storage_path;
    if (!path) {
      toast.error("Nothing to download yet — generate a flyer first.");
      return;
    }
    // Lazy Registration gate — guests must sign up to download. We persist the
    // requested storage_path in the pendingAction so it downloads automatically
    // after the OAuth round-trip.
    if (isGuest) {
      setGuestAuth({
        open: true,
        trigger: "download",
        pendingAction: { kind: "download", storage_path: path },
      });
      return;
    }
    const url = `${fileUrl(path)}?download=1&filename=ai-pro-studio-flyer.png`;
    try {
      const a = document.createElement("a");
      a.href = url;
      a.download = "ai-pro-studio-flyer.png";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => a.remove(), 1000);
    } catch (_) {
      // Absolute last resort — open in a new tab; the attachment header still downloads it.
      window.open(url, "_blank", "noopener");
    }
  };

  const openInEditor = async () => {
    if (!result) return;
    // Lazy Registration gate — "Open in Editor" persists the design in the
    // user's account and requires auth. Guests get the signup modal with
    // the pending action so we can restart the export right after signup.
    if (isGuest) {
      setGuestAuth({
        open: true,
        trigger: "export",
        pendingAction: { kind: "export", storage_path: result.storage_path },
      });
      return;
    }
    setOpening(true);
    try {
      const el = newImageElement(fileUrl(result.storage_path), {
        x: 0, y: 0, width: result.width, height: result.height, zIndex: 1, name: "AI Pro Flyer",
      });
      const res = await api.post("/designs", {
        title: "AI Pro Studio Flyer",
        width: result.width, height: result.height,
        background: "#000000", elements: [el], category: "flyer",
      });
      navigate(`/editor/${res.data.id}`);
    } catch (e) {
      toast.error("Could not open the design in the editor.");
      setOpening(false);
    }
  };

  const openSaveToWorkspace = () => {
    // Lazy Registration gate — Save to Workspace persists the design under
    // the user's account. Guests get the signup modal; after signup, the
    // guest render is already handed off and appears in their history, so
    // the button will work immediately on return.
    if (isGuest) {
      setGuestAuth({
        open: true,
        trigger: "save",
        pendingAction: { kind: "save_workspace", storage_path: result?.storage_path },
      });
      return;
    }
    setSaveWsOpen(true);
  };

  const openSaveToLibrary = () => {
    // Save to Library is admin-only for auth'd users; the button also
    // renders for guests as a signup lure. Non-admin authenticated users
    // never see it.
    if (isGuest) {
      setGuestAuth({
        open: true,
        trigger: "save",
        pendingAction: { kind: "save_library", storage_path: result?.storage_path },
      });
      return;
    }
    setSaveLibOpen(true);
  };

  return (
    <div data-testid="ai-pro-studio" className="min-h-screen bg-[#070707] text-white font-body">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="max-w-[1500px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(isGuest ? "/" : "/dashboard")} data-testid="aipro-back-btn" className="h-9 w-9 rounded-lg hover:bg-white/10 flex items-center justify-center transition-colors"><ArrowLeft size={18} /></button>
            <div className="flex items-center gap-2">
              <Sparkle size={20} weight="fill" className="text-fuchsia-400" />
              <span className="font-display font-extrabold text-lg tracking-tight">AI Pro Studio</span>
              <span className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-fuchsia-400/15 text-fuchsia-300 text-[10px] font-bold ring-1 ring-fuchsia-400/30">GPT Image 2</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isGuest ? (
              <>
                <span data-testid="aipro-guest-chip" className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-white/10 bg-white/5 text-sm">
                  <Lightning weight="fill" size={15} style={{ color: "#FFD60A" }} /> {guestUsed ? 0 : 1} <span className="text-zinc-500">free {guestUsed ? "credit used" : "credit"}</span>
                </span>
                <button
                  data-testid="aipro-guest-signup-btn"
                  onClick={() => setGuestAuth({ open: true, trigger: "second_generate", pendingAction: { kind: "generate" } })}
                  title="Sign up to unlock 9 more free credits"
                  className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-black text-sm font-bold transition-transform hover:scale-105"
                  style={{ backgroundColor: "#FFD60A" }}
                >
                  <Lightning weight="fill" size={15} /> <span className="hidden md:inline">Sign up · 9 free</span>
                </button>
              </>
            ) : (
              <>
                <button onClick={() => navigate("/library")} data-testid="aipro-nav-library-btn" className="hidden md:inline-flex items-center gap-1.5 h-9 px-3 rounded-full hover:bg-white/10 text-zinc-300 hover:text-white text-sm font-medium transition-colors">
                  <FilmSlate weight="fill" size={15} className="text-amber-300" /> Library
                </button>
                <button onClick={() => navigate("/workspace")} data-testid="aipro-nav-workspace-btn" className="hidden md:inline-flex items-center gap-1.5 h-9 px-3 rounded-full hover:bg-white/10 text-zinc-300 hover:text-white text-sm font-medium transition-colors">
                  <FolderOpen weight="fill" size={15} className="text-blue-300" /> Workspace
                </button>
                <span data-testid="aipro-credit-count" className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-white/10 bg-white/5 text-sm">
                  <Lightning weight="fill" size={15} style={{ color: "#FFD60A" }} /> {subInfo?.ai_remaining ?? "—"} <span className="text-zinc-500">credits</span>
                </span>
                <button onClick={() => setTopupOpen(true)} data-testid="aipro-topup-btn" title="Need more credits? Top-up now" className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-black text-sm font-bold transition-transform hover:scale-105" style={{ backgroundColor: "#FFD60A" }}>
                  <Lightning weight="fill" size={15} /> <span className="hidden md:inline">Top-Up</span>
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      <TopUpModal open={topupOpen} onClose={() => setTopupOpen(false)} onSuccess={refreshSub} />
      <GuestAuthModal
        open={guestAuth.open}
        trigger={guestAuth.trigger}
        pendingAction={guestAuth.pendingAction}
        onClose={() => setGuestAuth({ open: false, trigger: "", pendingAction: null })}
      />
      <Paywall
        open={paywall.open}
        trigger={paywall.trigger}
        onClose={closePaywall}
        onUpgrade={() => { closePaywall(); openUpgrade("Unlock 2K AI Pro Studio exports and a full monthly credit allowance."); }}
        onTopup={() => { closePaywall(); setTopupOpen(true); }}
      />

      <Dialog open={libraryOpen} onOpenChange={setLibraryOpen}>
        <DialogContent className="bg-[#0d0d0d] border-white/10 text-white max-w-4xl w-[95vw] max-h-[85vh] overflow-y-auto" data-testid="aipro-library-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Choose a Style from the Library</DialogTitle>
          </DialogHeader>
          <StylesLibraryBrowser
            admin={isAdmin}
            selectLabel="Use as Reference"
            onSelect={(style) => { setLibraryOpen(false); loadLibraryStyleAsReference(style); }}
          />
        </DialogContent>
      </Dialog>

      {isAdmin && (
        <SaveToLibraryModal open={saveLibOpen} onOpenChange={setSaveLibOpen} storagePath={result?.storage_path} />
      )}

      {!isGuest && (
        <SaveToWorkspaceModal
          open={saveWsOpen}
          onOpenChange={setSaveWsOpen}
          result={result}
          context={{ eventDetails, direction, aspect, tier, generationMode, director }}
        />
      )}

      {isAdmin && (
        <Dialog open={directorManagerOpen} onOpenChange={setDirectorManagerOpen}>
          <DialogContent className="bg-[#0d0d0d] border-white/10 text-white max-w-3xl w-[95vw] max-h-[85vh] overflow-y-auto" data-testid="director-manager-modal">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">Creative Director Prompts</DialogTitle>
            </DialogHeader>
            <CreativeDirectorManager onChanged={() => setDirectorVersion((v) => v + 1)} />
          </DialogContent>
        </Dialog>
      )}

      <main className="max-w-[1500px] mx-auto px-6 py-8 grid grid-cols-1 lg:grid-cols-[1fr_390px] gap-8">
        {/* LEFT: assets + result */}
        <div className="space-y-6">
          <div>
            <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight mb-1">Reference-led flyers, baked by AI ✨</h1>
            <p className="text-zinc-400 max-w-2xl text-sm">Drop a reference flyer — GPT Image 2 studies its style and renders a brand-new flyer with your text baked right in. Add your hero subjects and supporting cast, write your event details, and generate.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <ReferenceBox file={reference[0]} onPickFile={(f) => setReference([f])} onRemove={() => setReference([])} onOpenLibrary={() => setLibraryOpen(true)} />
            <UploadBox id="aipro-amateur" icon={PencilLine} label="Amateur Flyer" hint="Optional · we read its text into Event Details" accent="bg-amber-500" multiple={false} max={1} files={amateur} onAdd={onAmateurAdd} onRemove={removeAt(setAmateur)} busy={extracting} busyLabel="Reading text…" />
            <UploadBox id="aipro-hero" icon={Star} label="Hero / Main Subject(s)" hint="Up to 3 images" accent="bg-emerald-600" multiple max={3} files={hero} onAdd={addFiles(setHero, 3)} onRemove={removeAt(setHero)} />
            <UploadBox id="aipro-supporting" icon={UsersThree} label="Supporting Cast" hint="Up to 10 · hosts, DJs, openers" accent="bg-blue-600" multiple max={10} files={supporting} onAdd={addFiles(setSupporting, 10)} onRemove={removeAt(setSupporting)} />
          </div>

          {/* Library Director handoff chip — shows when a director was chosen in the Design Library */}
          {director?.fromLibrary && (
            <div data-testid="aipro-library-director-chip" className="flex items-center gap-2 rounded-xl border border-amber-400/40 bg-amber-400/10 px-4 py-2.5">
              <FilmSlate size={16} weight="fill" className="text-amber-300 shrink-0" />
              <p className="text-sm text-amber-100 truncate">
                <span className="font-bold">{director.name}</span>
                {(director.category_name || director.subcategory_name) && (
                  <span className="text-amber-200/70"> · {director.category_name}{director.subcategory_name ? ` → ${director.subcategory_name}` : ""}</span>
                )}
                <span className="text-amber-200/60"> — from the Design Library</span>
              </p>
              <button data-testid="aipro-library-director-clear" onClick={() => setDirector(null)} className="ml-auto h-6 w-6 rounded-full hover:bg-white/10 flex items-center justify-center text-amber-200 shrink-0"><X size={13} weight="bold" /></button>
            </div>
          )}

          {/* Creative Directors — admin-managed hidden prompt presets */}
          <CreativeDirectorPicker
            selected={director}
            onSelect={setDirector}
            isAdmin={isAdmin}
            onManage={() => setDirectorManagerOpen(true)}
            refreshKey={directorVersion}
          />

          {/* Result / preview */}
          <div className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-5 min-h-[360px] flex items-center justify-center">
            {running ? (
              <div className="text-center" data-testid="aipro-loading">
                <Sparkle size={56} weight="fill" className="text-fuchsia-500 animate-pulse mx-auto mb-4" />
                <p className="font-display text-lg font-bold">Generating flyer…</p>
                <p className="text-sm text-zinc-500 mt-1">GPT Image 2 is composing your design. This can take up to a couple of minutes for 2K.</p>
              </div>
            ) : result ? (
              <div className="w-full flex flex-col items-center" data-testid="aipro-result">
                <img src={fileUrl(result.storage_path)} alt="AI Pro flyer" className="max-h-[480px] rounded-xl ring-1 ring-white/15 shadow-2xl object-contain" />
                <p className="text-[11px] text-zinc-500 mt-3">{result.width}×{result.height} · {result.quality_tier?.toUpperCase()} · {result.aspect_ratio}</p>
                <div className="flex items-center gap-3 mt-5 flex-wrap justify-center">
                  <button data-testid="aipro-open-editor" onClick={openInEditor} disabled={opening} className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-fuchsia-600 hover:bg-fuchsia-500 text-white font-bold transition-colors disabled:opacity-60">
                    <PencilLine size={18} weight="bold" /> {opening ? "Opening…" : "Open in FlyerStudio Editor"}
                  </button>
                  <button data-testid="aipro-download" onClick={() => downloadFlyer()} className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-white/10 hover:bg-white/20 text-white font-semibold transition-colors">
                    <DownloadSimple size={18} weight="bold" /> Download Flyer
                  </button>
                  <button data-testid="aipro-rerun" onClick={generate} className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-white/5 hover:bg-white/10 text-zinc-300 font-semibold transition-colors">
                    <ArrowClockwise size={18} weight="bold" /> Rerun
                  </button>
                  <button data-testid="aipro-save-workspace" onClick={openSaveToWorkspace} className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-white/5 hover:bg-white/10 text-amber-200 font-semibold transition-colors">
                    <FolderOpen size={18} weight="fill" /> Save to Workspace
                  </button>
                  {(isAdmin || isGuest) && (
                    <button data-testid="aipro-save-library" onClick={openSaveToLibrary} className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-white/5 hover:bg-white/10 text-fuchsia-200 font-semibold transition-colors">
                      <BookmarkSimple size={18} weight="fill" /> Save to Library
                    </button>
                  )}
                </div>
                <DesignFeedback key={result.id || result.storage_path} jobId={result.id} />
              </div>
            ) : (
              <div className="text-center text-zinc-600">
                <Sparkle size={42} weight="duotone" className="mx-auto mb-3 text-zinc-700" />
                <p className="text-sm">Your generated flyer will appear here.</p>
              </div>
            )}
          </div>

          {/* Recent generations — nothing is ever lost, re-download anytime */}
          {history.length > 0 && (
            <div data-testid="aipro-history" className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-5">
              <div className="flex items-center gap-2 mb-3">
                <ClockCounterClockwise size={16} className="text-fuchsia-400" weight="bold" />
                <p className="text-sm font-semibold">Recent generations</p>
                <span className="text-[11px] text-zinc-500">· saved for you — click to view, hover to re-download</span>
              </div>
              <div className="flex gap-3 overflow-x-auto pb-2">
                {history.map((item, i) => (
                  <div
                    key={item.storage_path}
                    data-testid={`aipro-history-item-${i}`}
                    onClick={() => setResult(item)}
                    className="relative group shrink-0 w-24 h-32 rounded-lg overflow-hidden ring-1 ring-white/10 bg-black/40 cursor-pointer"
                  >
                    <img src={fileUrl(item.storage_path)} alt="previous generation" className="w-full h-full object-cover" />
                    <button
                      data-testid={`aipro-history-download-${i}`}
                      onClick={(e) => { e.stopPropagation(); downloadFlyer(item.storage_path); }}
                      title="Download this flyer"
                      className="absolute bottom-1 right-1 h-7 w-7 rounded-full bg-black/70 hover:bg-fuchsia-600 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <DownloadSimple size={14} weight="bold" className="text-white" />
                    </button>
                    {result?.storage_path === item.storage_path && <span className="absolute inset-0 ring-2 ring-fuchsia-500 rounded-lg pointer-events-none" />}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* RIGHT: controls */}
        <aside className="lg:sticky lg:top-24 h-fit rounded-2xl border border-white/10 bg-[#111] p-5 flex flex-col gap-5">
          <p className="font-display text-lg font-bold flex items-center gap-2"><Sparkle size={18} weight="fill" className="text-fuchsia-300" /> Generation Setup</p>

          <div className="flex flex-col">
            <label className="text-xs uppercase tracking-[0.16em] text-zinc-400 font-bold mb-2 block">Event Details</label>
            <textarea
              data-testid="aipro-event-details"
              value={eventDetails}
              onChange={(e) => setEventDetails(e.target.value)}
              placeholder="All text for the flyer — title, date, time, location, performers, contact…"
              rows={7}
              className="w-full rounded-xl bg-black/40 border border-white/10 px-3.5 py-3 text-sm leading-relaxed outline-none focus:border-fuchsia-500/60 resize-y min-h-[130px]"
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs uppercase tracking-[0.16em] text-zinc-400 font-bold mb-2 block">Design Direction <span className="text-zinc-600 normal-case font-normal">· optional</span></label>
            <textarea
              data-testid="aipro-design-direction"
              value={direction}
              onChange={(e) => setDirection(e.target.value)}
              placeholder="Surgical overrides, e.g. 'make the title gold', 'use the pose from subject 2', 'colors: hot pink + neon green'…"
              rows={4}
              className="w-full rounded-xl bg-black/40 border border-white/10 px-3.5 py-3 text-sm leading-relaxed outline-none focus:border-fuchsia-500/60 resize-y min-h-[90px]"
            />
          </div>

          {/* AI Design Brain / Layout Engine — hidden 2026-07. Plumbing preserved
              upstream (smartLayout state + use_layout_engine form field). To
              un-hide, restore the render below and default `smartLayout` to true. */}
          {/* <DesignBrainPanel config={brainConfig} enabled={smartLayout} onToggle={setSmartLayout} /> */}

          {/* Generation Strategy — 3-mode selector. Isolated by mode so each
              path behaves cleanly with no cross-contamination:
                • Reference-Driven → reference REQUIRED, existing pipeline
                • Creative Director → text-only, NO images sent to the model
                • Hybrid            → optional reference influence + prompt-led
              The Reference Flyer upload box + preview stay visible in ALL
              modes — the model just won't receive the file in Pure Text Mode. */}
          <div
            data-testid="aipro-mode"
            data-value={generationMode}
            className="flex flex-col rounded-xl bg-black border border-white/15 p-3.5 text-white"
          >
            <label className="text-xs uppercase tracking-[0.16em] text-white/80 font-bold mb-2 block">
              Generation Strategy
            </label>
            <div role="radiogroup" className="grid grid-cols-3 gap-2">
              {[
                { key: "reference",         short: "Reference",         desc: "Uses your uploaded flyer as the main design influence." },
                { key: "creative_director", short: "Creative Director", desc: "Generates from prompt intelligence and event details only." },
                { key: "hybrid",            short: "Hybrid",            desc: "Uses prompt intelligence with optional reference support." },
              ].map((opt) => {
                const active = generationMode === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    data-testid={`aipro-mode-${opt.key}`}
                    onClick={() => setGenerationMode(opt.key)}
                    className={`rounded-lg px-2 py-2.5 text-center transition-all ring-1 ${
                      active
                        ? "bg-fuchsia-600/20 ring-fuchsia-500 text-white"
                        : "bg-white/5 ring-white/10 text-zinc-300 hover:bg-white/10"
                    }`}
                  >
                    <span className="block text-[12px] font-bold leading-none">{opt.short}</span>
                  </button>
                );
              })}
            </div>
            <p data-testid="aipro-mode-hint" className="text-[11px] text-zinc-400 mt-2 leading-snug">
              {generationMode === "reference"         && "Reference-Driven: Uses your uploaded flyer as the main design influence."}
              {generationMode === "creative_director" && "Creative Director: Generates from prompt intelligence and event details only. Reference and hero uploads are not sent to the AI."}
              {generationMode === "hybrid"            && "Hybrid: Uses prompt intelligence with optional reference support. Reference is optional."}
            </p>
          </div>

          {/* Reference Influence Slider — Hybrid Mode only + reference required.
              0=inspiration, 25=light, 50=moderate, 75=strong, 100=close */}
          {generationMode === "hybrid" && reference.length > 0 && (
            <div data-testid="aipro-influence" className="flex flex-col rounded-xl bg-black border border-white/15 p-3.5 text-white">
              <div className="flex items-baseline justify-between mb-2">
                <label className="text-xs uppercase tracking-[0.16em] text-white/80 font-bold">Reference Influence</label>
                <span data-testid="aipro-influence-label" className="text-[11px] font-bold text-amber-300">
                  {referenceInfluence}% · {referenceInfluence >= 88 ? "Close influence"
                    : referenceInfluence >= 63 ? "Strong influence"
                    : referenceInfluence >= 38 ? "Moderate influence"
                    : referenceInfluence >= 13 ? "Light inspiration"
                    : "Loose inspiration"}
                </span>
              </div>
              <input
                data-testid="aipro-influence-slider"
                type="range" min="0" max="100" step="25"
                value={referenceInfluence}
                onChange={(e) => setReferenceInfluence(Number(e.target.value))}
                className="w-full accent-amber-400"
                aria-label="Reference influence"
              />
              <div className="flex justify-between text-[10px] text-zinc-500 mt-1 select-none">
                <span>Inspiration</span><span>Light</span><span>Moderate</span><span>Strong</span><span>Close</span>
              </div>
            </div>
          )}

          {/* Brand + Palette pickers — authenticated users only, optional. */}
          {user && (brands.length > 0 || palettes.length > 0) && (
            <div data-testid="aipro-brand-palette" className="flex flex-col rounded-xl bg-black border border-white/15 p-3.5 text-white gap-3">
              <label className="text-xs uppercase tracking-[0.16em] text-white/80 font-bold">Brand &amp; Palette</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {brands.length > 0 && (
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Brand system</label>
                    <select
                      data-testid="aipro-brand-select"
                      value={brandId}
                      onChange={(e) => setBrandId(e.target.value)}
                      className="w-full rounded-lg bg-black/40 border border-white/15 px-3 py-2 text-sm outline-none focus:border-amber-400/60"
                    >
                      <option value="">— none —</option>
                      {brands.map((b) => <option key={b.id} value={b.id}>{b.name}{b.is_default ? " (default)" : ""}</option>)}
                    </select>
                  </div>
                )}
                {palettes.length > 0 && (
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Color palette</label>
                    <select
                      data-testid="aipro-palette-select"
                      value={paletteId}
                      onChange={(e) => setPaletteId(e.target.value)}
                      className="w-full rounded-lg bg-black/40 border border-white/15 px-3 py-2 text-sm outline-none focus:border-amber-400/60"
                    >
                      <option value="">— none —</option>
                      {palettes.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.colors?.length || 0} colors</option>)}
                    </select>
                  </div>
                )}
              </div>
              {(brandId || paletteId) && (
                <div className="text-[11px] text-zinc-500 flex items-center gap-3">
                  {brandId && (() => {
                    const b = brands.find((x) => x.id === brandId);
                    return b ? (
                      <span data-testid="aipro-brand-preview" className="inline-flex items-center gap-1.5">
                        <span className="w-3 h-3 rounded-sm border border-white/10" style={{ background: b.primary_color || "#333" }} />
                        <span className="text-zinc-300">{b.name}</span>
                      </span>
                    ) : null;
                  })()}
                  {paletteId && (() => {
                    const p = palettes.find((x) => x.id === paletteId);
                    return p ? (
                      <span data-testid="aipro-palette-preview" className="inline-flex items-center gap-1.5">
                        <span className="inline-flex rounded-sm overflow-hidden border border-white/10">
                          {(p.colors || []).slice(0, 6).map((c, i) => (
                            <span key={i} className="w-3 h-3" style={{ background: c.hex }} />
                          ))}
                        </span>
                        <span className="text-zinc-300">{p.name}</span>
                      </span>
                    ) : null;
                  })()}
                </div>
              )}
              <p className="text-[10px] text-zinc-600 leading-snug">Applied to every mode. The AI will honor your brand colors, fonts, and palette when generating.</p>
            </div>
          )}

          <div className="flex flex-col rounded-xl bg-black border border-white/15 p-3.5 text-white" data-testid="aipro-aspect" data-value={aspect}>
            <label className="text-xs uppercase tracking-[0.16em] text-white/80 font-bold mb-3 block">Aspect Ratio</label>
            <div className="grid grid-cols-3 gap-2">
              {ASPECT_OPTIONS.map(({ v, label }) => {
                const active = aspect === v;
                return (
                  <button
                    key={v}
                    type="button"
                    data-testid={`aipro-aspect-${v}`}
                    aria-pressed={active}
                    onClick={() => setAspect(v)}
                    className={`flex flex-col items-center justify-center gap-1.5 rounded-lg px-1 py-2.5 border transition-colors ${
                      active
                        ? "border-fuchsia-500 bg-fuchsia-500/15 text-white"
                        : "border-white/10 bg-white/[0.03] text-zinc-300 hover:border-white/30 hover:text-white"
                    }`}
                  >
                    <RatioShape v={v} />
                    <span className="text-[12px] font-bold leading-none">{v}</span>
                    <span className={`text-[10px] leading-none ${active ? "text-white/70" : "text-zinc-500"}`}>{label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col">
            <label className="text-xs uppercase tracking-[0.16em] text-zinc-400 font-bold mb-2 block">Quality Tier</label>
            <div data-testid="aipro-quality" className="grid grid-cols-2 gap-2">
              {TIERS.map((t) => {
                const active = tier === t.key;
                const locked = t.proOnly && !canUsePro;
                return (
                  <button
                    key={t.key}
                    data-testid={`aipro-tier-${t.key}`}
                    onClick={() => pickTier(t)}
                    className={`relative rounded-xl px-3 py-3.5 text-center transition-all ring-1 ${active ? "bg-fuchsia-600/20 ring-fuchsia-500 text-white" : "bg-white/5 ring-white/10 text-zinc-300 hover:bg-white/10"}`}
                  >
                    {locked && <Lock size={11} weight="fill" className="absolute top-1.5 right-1.5 text-amber-300" />}
                    <span className="block font-display font-black text-xl leading-none">{t.res}</span>
                    <span className="block text-[10px] text-zinc-400 mt-1.5 leading-tight">{t.label}</span>
                    <span
                      data-testid={`aipro-tier-${t.key}-cost`}
                      className="mt-2 inline-flex items-center justify-center gap-1 px-2 py-0.5 rounded-full bg-black/40 ring-1 ring-white/10 text-[10px] font-bold"
                      style={{ color: "#FFD60A" }}
                    >
                      <Lightning weight="fill" size={10} />
                      {t.credits} <span className="text-zinc-400 font-normal">credit{t.credits === 1 ? "" : "s"}</span>
                    </span>
                  </button>
                );
              })}
            </div>
            <p data-testid="aipro-tier-hint" className="text-[11px] text-zinc-500 mt-2 leading-snug">
              Credits are only charged on a successful generation. <span className="text-zinc-600">1K = 1 credit · 2K = 4 credits.</span>
            </p>
          </div>

          <button
            data-testid="aipro-generate-btn"
            onClick={generate}
            disabled={running}
            className="w-full h-12 rounded-xl bg-gradient-to-r from-fuchsia-600 to-purple-600 hover:from-fuchsia-500 hover:to-purple-500 text-white font-bold text-sm flex items-center justify-center gap-2 transition-all disabled:opacity-60 shadow-[0_8px_30px_rgba(192,38,211,0.35)]"
          >
            {running ? <CircleNotch size={18} className="animate-spin" /> : <Sparkle size={18} weight="fill" />}
            {running ? "Generating flyer…" : (
              <>
                Generate
                <span
                  data-testid="aipro-generate-cost"
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/30 ring-1 ring-white/20 text-[11px] font-bold ml-1"
                >
                  <Lightning weight="fill" size={11} style={{ color: "#FFD60A" }} />
                  {TIERS.find((t) => t.key === tier)?.credits || 1}
                </span>
              </>
            )}
          </button>
          {reference.length === 0 && generationMode === "reference" && (
            <p className="text-[11px] text-zinc-500 text-center -mt-2">Add a reference flyer to enable generation.</p>
          )}

          <DevDebugPanel
            isAdmin={isAdmin}
            reference={reference}
            hero={hero}
            supporting={supporting}
            eventDetails={eventDetails}
            direction={direction}
            aspect={aspect}
            tier={tier}
            generationMode={generationMode}
            director={director}
            debugPayload={debugPayload}
            setDebugPayload={setDebugPayload}
            debugLoading={debugLoading}
            setDebugLoading={setDebugLoading}
            referenceInfluence={referenceInfluence}
            brandId={brandId}
            paletteId={paletteId}
          />
        </aside>
      </main>
    </div>
  );
}
